/**
 * RoleSection — one crew position's header, its assigned members, and its Add affordance.
 *
 * Extracted from CrewSlotEditor so each file declares one component
 * (`react/no-multi-comp`); behaviour is unchanged.
 */
import type { CrewPosition } from '../../../../api/workshops';
import { Avatar } from '../../primitives';

import { AddRoleButton } from './AddRoleButton';
import { ROLE_CONFIG, type DraftMember } from './crewModel';

export function RoleSection({
  position,
  members,
  readOnly,
  published,
  canAdd,
  onRemove,
  onAdd,
}: {
  position: CrewPosition;
  members: DraftMember[];
  readOnly: boolean;
  published: boolean;
  canAdd: boolean;
  onRemove: (userId: string) => void;
  onAdd: () => void;
}) {
  const cfg = ROLE_CONFIG[position];
  const empty = members.length === 0;
  // BON-466: an unfilled required slot is only URGENT once the workshop is
  // published. On a draft it is an ordinary to-do — publishing no longer waits
  // on it, so alarming the operator here would contradict the publish flow.
  const needsTeacher = cfg.required && empty && published;
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: needsTeacher ? '#c0392b' : 'var(--nb-subtle)',
          marginBottom: 6,
        }}
      >
        {cfg.label} · {members.length} assigned · {cfg.required ? 'needed to run' : 'optional'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {members.map((m) => (
          <div
            key={m.userId}
            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '3px 0' }}
          >
            <Avatar name={m.name} size={24} />
            <span style={{ flex: 1, fontSize: 13, color: 'var(--nb-ink)' }}>{m.name}</span>
            {!readOnly && (
              <button
                type="button"
                className="nb-btn nb-btn-ghost"
                aria-label={`Remove ${m.name}`}
                onClick={() => onRemove(m.userId)}
                style={{ height: 24, padding: '0 8px' }}
              >
                ×
              </button>
            )}
          </div>
        ))}
        {!readOnly && canAdd && (
          <AddRoleButton noun={cfg.noun} emphasized={needsTeacher} onClick={onAdd} />
        )}
      </div>
    </div>
  );
}
